import {useMemo} from 'react';
import {Node, OnEdgesChange, OnNodesChange} from 'reactflow';

import {assert} from 'Asserts';
import {ProcedureType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {ExperimentMethod} from 'studies/typed/shared/procedure-runs/design-state/ExperimentalInputs';
import {ConditionGroup} from 'studies/typed/shared/procedure-runs/design-state/useExperimentDesignState';
import {
  hideFlowchartElementBeforeLayout,
  isFlowchartElementHiddenBeforeLayout,
  unhideFlowchartElementAfterLayout,
} from 'ui-library/reactflow/layout/hideFlowchartElementBeforeLayoutUtils';

import {ProcedureRunDesignFlowchartAddReplicateNodeData} from './ProcedureRunDesignFlowchartAddReplicateNode';
import {ProcedureRunDesignFlowchartColumnNodeData} from './ProcedureRunDesignFlowchartColumnNode';
import {getNodeConnectionRoles} from './procedureRunDesignFlowchartConnectionUtils';
import {ProcedureRunDesignFlowchartGhostNodeData} from './ProcedureRunDesignFlowchartGhostNode';
import {
  FlowchartEdgeSpec,
  filterEdgesTouchingRemovedNodes,
  GridLayoutOptions,
  makeAddReplicateRow,
  makeFlowchartEdges,
  makeInitialNodes,
  StatusByNodeId,
} from './ProcedureRunDesignFlowchartLayoutUtils';
import {ProcedureRunDesignFlowchartNodeData} from './ProcedureRunDesignFlowchartNode';
import {useProcedureRunDesignFlowchartConnectionRoles} from './useProcedureRunDesignFlowchartConnectionRoles';
import {
  ProcedureRunDesignFlowchartLayoutEdge,
  useProcedureRunDesignFlowchartEdges,
} from './useProcedureRunDesignFlowchartEdges';
import type {MethodHighlightState} from './useProcedureRunDesignFlowchartHighlighting';
import {useProcedureRunDesignFlowchartHighlighting} from './useProcedureRunDesignFlowchartHighlighting';
import {useProcedureRunDesignFlowchartMeasuredLayout} from './useProcedureRunDesignFlowchartMeasuredLayout';
import {useProcedureRunDesignFlowchartNodeRemovalState} from './useProcedureRunDesignFlowchartNodeRemovalState';
import {useProcedureRunDesignFlowchartNodes} from './useProcedureRunDesignFlowchartNodes';

// Define a single empty set to avoid unnecessary re-renders.
const EMPTY_SET: ReadonlySet<string> = new Set();

export interface ProcedureRunDesignFlowchartLayoutOptions extends GridLayoutOptions {
  columnHeaderBottomGap: number;
}

export type ProcedureRunDesignFlowchartLayoutNodeData =
  | ProcedureRunDesignFlowchartNodeData
  | ProcedureRunDesignFlowchartColumnNodeData
  | ProcedureRunDesignFlowchartAddReplicateNodeData
  | ProcedureRunDesignFlowchartGhostNodeData;
export type ProcedureRunDesignFlowchartLayoutNode = Node<ProcedureRunDesignFlowchartLayoutNodeData>;

export interface UseProcedureRunDesignFlowchartLayoutArgs {
  conditions: Array<ConditionGroup>;
  methods: Array<ExperimentMethod>;
  activeMethodId: string | null;
  procedureType: ProcedureType;
  factorsSetByMethodId: Record<string, number>;
  layoutOptions: ProcedureRunDesignFlowchartLayoutOptions;
  methodHighlightState: MethodHighlightState;
  // When false, persisted conditions render with locked icon
  allowEditingPersistedConditions: boolean;
  statusByNodeId?: StatusByNodeId;
  // The single edge list to render (derived + drawn, already merged upstream).
  edgeSpecs: Array<FlowchartEdgeSpec>;
  /** When true, render archived cells standalone with no edges or status. Otherwise hide them. */
  showArchived?: boolean;
  // Whether the flowchart is being edited. Gates the add-replicate preview row.
  isEditable: boolean;
  pendingRemovalNodeIds?: ReadonlySet<string>;
  removedNodeIds?: ReadonlySet<string>;
}

export interface UseProcedureRunDesignFlowchartLayoutResult {
  nodes: Array<ProcedureRunDesignFlowchartLayoutNode>;
  edges: Array<ProcedureRunDesignFlowchartLayoutEdge>;
  onNodesChange: OnNodesChange;
  onEdgesChange: OnEdgesChange;
}

/**
 * Manages the two-phase layout for ProcedureRunDesignFlowchart:
 * 1. Create hidden placeholder nodes so ReactFlow can measure their dimensions.
 * 2. Once dimensions are available, compute grid positions and unhide nodes.
 */
export function useProcedureRunDesignFlowchartLayout({
  conditions,
  methods,
  activeMethodId,
  procedureType,
  factorsSetByMethodId,
  layoutOptions,
  methodHighlightState,
  allowEditingPersistedConditions,
  statusByNodeId,
  edgeSpecs,
  showArchived = false,
  isEditable,
  pendingRemovalNodeIds = EMPTY_SET,
  removedNodeIds = EMPTY_SET,
}: UseProcedureRunDesignFlowchartLayoutArgs): UseProcedureRunDesignFlowchartLayoutResult {
  // Once a procedure (with methods) is attached, a procedure run design always has at least one
  // condition. The procedure-less empty state renders no methods, so it needs no conditions.
  assert(methods.length === 0 || conditions.length >= 1, 'Expected at least one condition');

  const {columnNodes: initialColumnNodes, rows: gridRows} = useMemo(
    () =>
      makeInitialNodes({
        conditionGroups: conditions,
        methods,
        allowEditingPersistedConditions,
        procedureType,
        factorsSetByMethodId,
        activeMethodId,
        statusByNodeId,
        showArchived,
        edgeSpecs,
      }),
    [
      conditions,
      methods,
      allowEditingPersistedConditions,
      activeMethodId,
      procedureType,
      factorsSetByMethodId,
      statusByNodeId,
      showArchived,
      edgeSpecs,
    ]
  );

  // The add-replicate preview row renders only while editing a split-pool run (active method present).
  const addReplicateRow = useMemo(
    () =>
      isEditable && activeMethodId != null
        ? makeAddReplicateRow({methods, activeMethodId})
        : {row: [], previewEdgeSpecs: []},
    [isEditable, activeMethodId, methods]
  );

  // Append the preview row as the last band so it lands below the deepest column's last real cell.
  const initialRows = useMemo(
    (): Array<Array<ProcedureRunDesignFlowchartLayoutNode>> =>
      addReplicateRow.row.length > 0 ? [...gridRows, addReplicateRow.row] : gridRows,
    [gridRows, addReplicateRow.row]
  );

  const {nodes, setNodes, onNodesChange} = useProcedureRunDesignFlowchartNodes({
    initialColumnNodes,
    initialRows,
  });

  // Roles depend only on methods and the active layer, not live edges. Recomputing here
  // keeps removability in sync with the drawing affordance.
  const roleByNodeId = useMemo(
    // `removedNodeIds` is required by the helper, but static roles do not depend on it, so pass the
    // stable empty set. Removed-node filtering for persistent source-handle visibility is recomputed
    // in useProcedureRunDesignFlowchartConnectionRoles with the live removedNodeIds.
    () =>
      getNodeConnectionRoles({
        conditions,
        methods,
        edges: [],
        activeMethodId,
        removedNodeIds: EMPTY_SET,
      }).roleByNodeId,
    [conditions, methods, activeMethodId]
  );

  // Preview specs carry synthetic ids absent from roleByNodeId, so their edges stay non-removable.
  const initialEdges = useMemo(
    () =>
      filterEdgesTouchingRemovedNodes(
        makeFlowchartEdges([...edgeSpecs, ...addReplicateRow.previewEdgeSpecs], roleByNodeId),
        removedNodeIds
      ),
    [edgeSpecs, addReplicateRow.previewEdgeSpecs, roleByNodeId, removedNodeIds]
  );

  const {edges, onEdgesChange} = useProcedureRunDesignFlowchartEdges({initialEdges});

  useProcedureRunDesignFlowchartHighlighting({
    methodHighlightState,
    initialColumnNodes,
    initialRows,
    setNodes,
  });

  useProcedureRunDesignFlowchartNodeRemovalState({
    pendingRemovalNodeIds,
    removedNodeIds,
    initialColumnNodes,
    initialRows,
    setNodes,
  });

  useProcedureRunDesignFlowchartConnectionRoles({
    conditions,
    methods,
    activeMethodId,
    edges,
    removedNodeIds,
    initialColumnNodes,
    initialRows,
    setNodes,
  });

  useProcedureRunDesignFlowchartMeasuredLayout({
    initialColumnNodes,
    initialRows,
    layoutOptions,
    nodes,
    setNodes,
  });

  // An edge is shown only once both its endpoint nodes are laid out. Deriving visibility from the
  // nodes, rather than toggling edge opacity in an effect, keeps edges from flashing before their
  // nodes are positioned and reveals edges drawn after layout settles.
  const nodeVisibilityHash = getNodeVisibilityHash(nodes);
  const renderedEdges = useMemo(() => {
    const isNodeShownById = new Map(
      nodes.map((node) => [node.id, !isFlowchartElementHiddenBeforeLayout(node)])
    );
    return edges.map((edge) => {
      const isShown =
        (isNodeShownById.get(edge.source) ?? false) && (isNodeShownById.get(edge.target) ?? false);
      return isShown ? unhideFlowchartElementAfterLayout(edge) : hideFlowchartElementBeforeLayout(edge);
    });
    // Depend on the visibility hash, not `nodes`, so edges don't rebuild on unrelated node changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [edges, nodeVisibilityHash]);

  return {nodes, edges: renderedEdges, onNodesChange, onEdgesChange};
}

/** Fingerprint of each node's id + shown/hidden state, so edges recompute only when visibility changes. */
function getNodeVisibilityHash(nodes: Array<ProcedureRunDesignFlowchartLayoutNode>): string {
  return nodes
    .map((node) => `${node.id}:${isFlowchartElementHiddenBeforeLayout(node) ? '0' : '1'}`)
    .join('|');
}
